import { useState } from 'react';
import { ListRange } from 'react-virtuoso';
import { usePublicApplication } from '../../../../../hooks/usePublicApplication';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import { returnOrderIdBasedOnPublicOrder } from '../../../../../utils/returnOrderIdBasedOnPublicOrder';
import useOrderBook from '../../../../order-book/hooks/useOrderBook';
import CatalogProductsManager from '../../CatalogProductsManager';
import {
  CatalogProductsListV2Result,
  RowCards,
  UseProductCollectionListParams,
} from '../catalogProductsListV2.types';
import { buildResolvedRows } from '../utils/catalogDragAndDrop.utils';
import {
  buildSectionItemOffsets,
  computeAnchorPage,
  pageFromSnapshot,
  resolveRowCards,
} from '../utils/catalogRowMapping.utils';
import { useCatalogAccessSuggestions } from './useCatalogAccessSuggestions';
import { useCatalogSellout } from './useCatalogSellout';

export const useProductCollectionList = (
  params: UseProductCollectionListParams,
): CatalogProductsListV2Result => {
  const {
    collectionId,
    source,
    anchorPage,
    maxItemsPerRow,
    pageSize,
    isOfflineMode,
    onAnchorPageChange,
    getSnapshot,
  } = params;

  const selectedOrder = useOrderBookStore((store) => store.selectedOrder);
  const { customerIds } = useOrderBook();
  const { sharedEntity } = usePublicApplication();

  const [seededCollectionId, setSeededCollectionId] = useState<string>();

  const mainCustomerId = selectedOrder?.customer?.id;
  const orderId = returnOrderIdBasedOnPublicOrder(sharedEntity, selectedOrder?.id);
  const { groups, pagesById } = source;

  const groupCounts = groups.map((group) => Math.ceil(group.itemsCount / maxItemsPerRow));
  const sectionLimits = CatalogProductsManager.calculateStartEndSectionsIndex(
    groups,
    maxItemsPerRow,
  );
  const sectionItemOffsets = buildSectionItemOffsets(groups);

  const sellout = useCatalogSellout({ pagesById, anchorPage, orderId });

  const access = useCatalogAccessSuggestions({
    pagesById,
    anchorPage,
    customerIds,
    isOfflineMode,
    mainCustomerId,
  });

  if (source.status === 'ready' && seededCollectionId !== collectionId) {
    setSeededCollectionId(collectionId);

    const seededPage = pageFromSnapshot({
      snapshot: getSnapshot(),
      maxItemsPerRow,
      pageSize,
      sectionLimits,
      sectionItemOffsets,
    });

    onAnchorPageChange(seededPage ?? 0);
  }

  if (source.status === 'error') {
    return { status: 'list-error', retry: source.retry };
  }

  if (source.status === 'loading') {
    return { status: 'loading' };
  }

  const resolveRow = (rowIndex: number): RowCards =>
    resolveRowCards({
      rowIndex,
      maxItemsPerRow,
      pageSize,
      sectionLimits,
      sectionItemOffsets,
      pagesById,
      sellout,
      access,
    });

  const onRangeChanged = (range: ListRange): void => {
    onAnchorPageChange(
      computeAnchorPage({ range, maxItemsPerRow, pageSize, sectionLimits, sectionItemOffsets }),
    );
  };

  return {
    status: 'ready',
    groupCounts,
    sectionLimits,
    sections: groups,
    resolvedRows: buildResolvedRows(pagesById),
    resolveRow,
    onRangeChanged,
  };
};
